// ============================================================================
// Schedule Event Service
// Manages schedule events for display on the reservation schedule grid
// Uses localStorage for persistence (display-only, no backend API needed)
// ============================================================================

import {
  ScheduleEvent,
  ScheduleEventCreate,
  ScheduleEventUpdate,
} from '../types';
import { authService } from './auth/authService';

const STORAGE_KEY = 'hcp_schedule_events';

function getStoreKey(): string {
  const storeId = authService.getStoreId();
  return `${STORAGE_KEY}_${storeId || 'default'}`;
}

function loadFromStorage(): ScheduleEvent[] {
  try {
    const raw = localStorage.getItem(getStoreKey());
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToStorage(events: ScheduleEvent[]): void {
  localStorage.setItem(getStoreKey(), JSON.stringify(events));
}

let nextId = Date.now();

export async function getScheduleEvents(activeOnly?: boolean): Promise<ScheduleEvent[]> {
  const events = loadFromStorage();
  if (activeOnly) {
    return events.filter(e => e.active);
  }
  return events;
}

export async function createScheduleEvent(data: ScheduleEventCreate): Promise<ScheduleEvent> {
  const events = loadFromStorage();
  const storeId = authService.getStoreId() || 0;
  const now = new Date().toISOString();
  const newEvent: ScheduleEvent = {
    id: nextId++,
    store_id: storeId,
    name: data.name,
    start_date: data.start_date,
    end_date: data.end_date,
    color: data.color,
    active: data.active ?? true,
    created_at: now,
    updated_at: now,
  };
  events.push(newEvent);
  saveToStorage(events);
  return newEvent;
}

export async function updateScheduleEvent(id: number, data: ScheduleEventUpdate): Promise<ScheduleEvent> {
  const events = loadFromStorage();
  const idx = events.findIndex(e => e.id === id);
  if (idx === -1) throw new Error('Event not found');

  const updated: ScheduleEvent = {
    ...events[idx],
    ...data,
    updated_at: new Date().toISOString(),
  };
  events[idx] = updated;
  saveToStorage(events);
  return updated;
}

export async function deleteScheduleEvent(id: number): Promise<void> {
  const events = loadFromStorage();
  const filtered = events.filter(e => e.id !== id);
  saveToStorage(filtered);
}

export const scheduleEventService = {
  getScheduleEvents,
  createScheduleEvent,
  updateScheduleEvent,
  deleteScheduleEvent,
};

export default scheduleEventService;
